import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import Collapse from '@mui/material/Collapse';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Autocomplete from '@mui/material/Autocomplete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import {
  getAtivosMAster,
  getGruposFundo,
  deleteAtivoMaster,
  deleteDeParaAtivo,
  deleteGrupoFundo,
  createDeParaAtivo,
  createContatoGrupoFundo,
  updateContatoGrupoFundo,
  deleteContatoGrupoFundo,
  updateGrupoFundo,
} from '../../../services/ativoMasterService';
import { getCarteiraAtivos } from '../../../services/carteiraDiariaService';
import { AtivoMaster, GrupoFundo, DeParaAtivo, ContatoGrupoFundo } from '../../../types/ativoMaster';
import { CarteiraDiariaAtivos } from '../../../types/carteiraDiaria';
import CadastrarAtivoMasterModal from '../../../components/CadastrarAtivoMasterModal';
import StatBox from '../../../components/StatBox';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const TIPO_COLORS: Record<string, { bg: string; color: string }> = {
  FIDC:   { bg: '#1565C0', color: '#fff' },
  FIM:    { bg: '#2E7D32', color: '#fff' },
  FIA:    { bg: '#6A1B9A', color: '#fff' },
  FIP:    { bg: '#E65100', color: '#fff' },
  FIRF:   { bg: '#00695C', color: '#fff' },
  FII:    { bg: '#4527A0', color: '#fff' },
  ETF:    { bg: '#0277BD', color: '#fff' },
  OUTROS: { bg: '#546E7A', color: '#fff' },
};

const getTipoChipSx = (tipo?: string) => {
  const c = tipo ? (TIPO_COLORS[tipo] ?? TIPO_COLORS.OUTROS) : null;
  if (!c) return {};
  return { backgroundColor: c.bg, color: c.color, fontWeight: 700, fontSize: '11px' };
};

const rentLabel = (ativo: AtivoMaster): string => {
  if (!ativo.rent_tipo) return '—';
  const periodo = ativo.rent_periodo === 'AM' ? 'a.m.' : 'a.a.';
  if (ativo.rent_tipo === 'FIXO') return `${ativo.rent_valor ?? '—'}% ${periodo}`;
  if (ativo.rent_tipo === 'IND_PERCENT') {
    const ind = ativo.rent_indicador_detail?.codigo ?? '?';
    return `${ativo.rent_valor ?? '—'}% do ${ind}`;
  }
  if (ativo.rent_tipo === 'IND_SPREAD') {
    const ind = ativo.rent_indicador_detail?.codigo ?? '?';
    return `${ind} + ${ativo.rent_valor ?? '—'}% ${periodo}`;
  }
  return '—';
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const thSx = {
  backgroundColor: '#F8FAFC',
  color: '#64748B',
  fontWeight: 600,
  fontFamily: 'Inter, sans-serif',
  fontSize: '11px',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.5px',
  borderBottom: '1px solid #E2E8F0',
  py: 1.5,
  whiteSpace: 'nowrap' as const,
};

const tdSx = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  color: '#1E293B',
  borderBottom: '1px solid #F1F5F9',
  py: 1,
};

// ─── AtivoRow ─────────────────────────────────────────────────────────────────

interface AtivoRowProps {
  ativo: AtivoMaster;
  onEdit: (ativo: AtivoMaster) => void;
  onDelete: (id: number) => void;
  onDeleteDePara: (id: number, ativoMasterId: number) => void;
  onAddDePara: (ativoMasterId: number, dp: DeParaAtivo) => void;
  initialExpanded?: boolean;
}

const AtivoRow: React.FC<AtivoRowProps> = ({ ativo, onEdit, onDelete, onDeleteDePara, onAddDePara, initialExpanded = false }) => {
  const [expanded, setExpanded] = useState(initialExpanded);
  const rowRef = useRef<HTMLTableRowElement>(null);

  useEffect(() => {
    if (!initialExpanded) return;
    // Aguarda a animação do accordion pai terminar antes de scrollar
    const timer = setTimeout(() => {
      rowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 350);
    return () => clearTimeout(timer);
  }, []);
  const [showAddForm, setShowAddForm] = useState(false);
  const [carteiraOpcoes, setCarteiraOpcoes] = useState<CarteiraDiariaAtivos[]>([]);
  const [buscandoCarteira, setBuscandoCarteira] = useState(false);
  const [entradaSelecionada, setEntradaSelecionada] = useState<CarteiraDiariaAtivos | null>(null);
  const [savingDp, setSavingDp] = useState(false);
  const [dpErro, setDpErro] = useState<string | null>(null);
  const searchTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCarteiraBusca = (_: React.SyntheticEvent, value: string) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (value.length < 2) { setCarteiraOpcoes([]); setBuscandoCarteira(false); return; }
    setBuscandoCarteira(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const r = await getCarteiraAtivos(0, 30, [], value);
        // deduplica por (adm, codigo1, nome)
        const seen = new Set<string>();
        const unique = r.results.filter(e => {
          const key = `${e.adm}|${e.codigo1 ?? ''}|${e.nome}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        setCarteiraOpcoes(unique);
      } catch { /* silencia */ }
      setBuscandoCarteira(false);
    }, 400);
  };

  const handleSalvarDePara = async () => {
    if (!entradaSelecionada) return;
    setSavingDp(true);
    setDpErro(null);
    try {
      const dp = await createDeParaAtivo({
        ativo_master: ativo.id,
        administradora: entradaSelecionada.adm,
        codigo_bruto: entradaSelecionada.codigo1 ?? '',
        nome_bruto: entradaSelecionada.nome,
      });
      onAddDePara(ativo.id, dp);
      setShowAddForm(false);
      setEntradaSelecionada(null);
      setCarteiraOpcoes([]);
    } catch {
      setDpErro('Erro ao salvar mapeamento.');
    } finally {
      setSavingDp(false);
    }
  };

  return (
    <>
      <TableRow
        ref={rowRef}
        onClick={() => setExpanded(v => !v)}
        sx={{
          cursor: 'pointer',
          '&:hover': { backgroundColor: '#F0F7FF' },
          '& > td': { borderBottom: expanded ? 'none' : undefined },
        }}
      >
        {/* Expand */}
        <TableCell sx={{ ...tdSx, width: 44, px: 0.5 }}>
          <IconButton size="small" onClick={e => { e.stopPropagation(); setExpanded(v => !v); }}
            sx={{ color: '#94A3B8', '&:hover': { color: '#2C91AD' } }}>
            {expanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>

        {/* Nome + condomínio */}
        <TableCell sx={{ ...tdSx, maxWidth: 280 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, overflow: 'hidden' }}>
            <Tooltip title={ativo.nome_canonico} placement="top" arrow>
              <Typography sx={{
                fontFamily: 'Inter', fontSize: '13px', fontWeight: 500, color: '#1E293B',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                flex: 1, minWidth: 0, cursor: 'default',
              }}>
                {ativo.nome_canonico}
              </Typography>
            </Tooltip>
            {ativo.condominio_display && (
              <Chip label={ativo.condominio_display} size="small" sx={{
                fontSize: '10px', height: 18, fontWeight: 600, flexShrink: 0,
                backgroundColor: ativo.condominio === 'FECHADO' || ativo.condominio_display === 'Fechado' ? '#F3EFF9' : '#EBF7FA',
                color:           ativo.condominio === 'FECHADO' || ativo.condominio_display === 'Fechado' ? '#7B5EA7' : '#2C91AD',
                border: `1px solid ${ativo.condominio === 'FECHADO' || ativo.condominio_display === 'Fechado' ? '#C4AEE8' : '#9DD8E8'}`,
              }} />
            )}
          </Stack>
        </TableCell>

        {/* Classe FIDC */}
        <TableCell sx={tdSx}>
          {ativo.classe_fidc_display
            ? <Chip label={ativo.classe_fidc_display} size="small"
                sx={{ fontSize: '11px', fontWeight: 600, backgroundColor: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }} />
            : <Typography sx={{ color: '#94A3B8', fontSize: '13px' }}>—</Typography>}
        </TableCell>

        {/* Subclasse */}
        <TableCell sx={{ ...tdSx, color: '#64748B', maxWidth: 130, overflow: 'hidden' }}>
          {ativo.subclasse
            ? (
              <Tooltip title={ativo.subclasse} placement="top" arrow>
                <Typography sx={{
                  fontSize: '13px', color: '#64748B', cursor: 'default',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {ativo.subclasse}
                </Typography>
              </Tooltip>
            )
            : <Typography sx={{ color: '#94A3B8', fontSize: '13px' }}>—</Typography>
          }
        </TableCell>

        {/* Rentabilidade */}
        <TableCell sx={tdSx}>
          <Typography sx={{ fontFamily: 'Inter', fontSize: '12px', fontWeight: 500, color: '#0F766E' }}>
            {rentLabel(ativo)}
          </Typography>
        </TableCell>

        {/* Prazo */}
        <TableCell sx={{ ...tdSx, color: '#64748B', fontSize: '12px' }}>
          {ativo.prazo_resgate || <Typography sx={{ color: '#94A3B8', fontSize: '13px' }}>—</Typography>}
        </TableCell>

        {/* Cronograma */}
        <TableCell sx={tdSx}>
          {ativo.cronogramas && ativo.cronogramas.length > 0 ? (
            <Stack spacing={0.5}>
              {ativo.cronogramas.map(c => (
                <Typography key={c.tipo} sx={{ fontSize: '12px', fontFamily: 'Inter', color: '#475569' }}>
                  <span style={{ fontWeight: 600 }}>{c.tipo_display}</span>
                  {' · '}
                  {c.periodo_display}
                </Typography>
              ))}
            </Stack>
          ) : (
            <Typography sx={{ color: '#94A3B8', fontSize: '13px' }}>—</Typography>
          )}
        </TableCell>

        {/* Ações */}
        <TableCell sx={{ ...tdSx, width: 80, pr: 1 }}>
          <Stack direction="row" spacing={0.25} justifyContent="flex-end">
            <Tooltip title="Editar ativo">
              <IconButton size="small" onClick={e => { e.stopPropagation(); onEdit(ativo); }}
                sx={{ color: '#94A3B8', '&:hover': { color: '#2C91AD', backgroundColor: '#EFF6FF' } }}>
                <EditIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Excluir ativo">
              <IconButton size="small"
                onClick={e => { e.stopPropagation(); if (window.confirm(`Excluir "${ativo.nome_canonico}"?`)) onDelete(ativo.id); }}
                sx={{ color: '#94A3B8', '&:hover': { color: '#DC2626', backgroundColor: '#FEF2F2' } }}>
                <DeleteIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </TableCell>
      </TableRow>

      {/* Linha de De-Para expandida */}
      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0, border: 'none', backgroundColor: '#FAFAFA' }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #E2E8F0' }}>
              <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#64748B',
                textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.5 }}>
                Mapeamentos De-Para
              </Typography>

              {ativo.de_para.length === 0 && !showAddForm && (
                <Typography sx={{ fontSize: '12px', color: '#94A3B8', fontStyle: 'italic', mb: 1 }}>
                  Nenhum mapeamento. Use o ⚠ na Carteira Diária ou adicione abaixo.
                </Typography>
              )}

              {ativo.de_para.length > 0 && (
                <Stack spacing={0.75} mb={1}>
                  {ativo.de_para.map((dp: DeParaAtivo) => (
                    <Stack key={dp.id} direction="row" alignItems="center" spacing={1.5}
                      sx={{
                        px: 1.5, py: 1, borderRadius: 1.5,
                        backgroundColor: '#fff', border: '1px solid #E2E8F0',
                        '&:hover': { borderColor: '#CBD5E1', backgroundColor: '#F8FAFC' },
                      }}
                    >
                      <Chip label={dp.administradora} size="small"
                        sx={{ fontSize: '11px', fontWeight: 700, backgroundColor: '#1E293B', color: '#fff', height: 22 }} />
                      <Typography sx={{ fontSize: '12px', fontFamily: 'monospace', color: '#475569', fontWeight: 500 }}>
                        {dp.codigo_bruto}
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: '#94A3B8' }}>→</Typography>
                      <Typography sx={{ fontSize: '12px', color: '#475569', flex: 1,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {dp.nome_bruto}
                      </Typography>
                      <Tooltip title="Remover mapeamento">
                        <IconButton size="small"
                          onClick={() => { if (window.confirm('Remover este mapeamento?')) onDeleteDePara(dp.id, ativo.id); }}
                          sx={{ color: '#94A3B8', '&:hover': { color: '#DC2626', backgroundColor: '#FEF2F2' }, p: 0.5 }}>
                          <DeleteIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ))}
                </Stack>
              )}

              {/* Inline add form */}
              {showAddForm ? (
                <Box sx={{ p: 1.5, border: '1px dashed #CBD5E1', borderRadius: 1.5, backgroundColor: '#F8FAFC', mt: 0.5 }}>
                  <Stack spacing={1.5}>
                    <Autocomplete
                      options={carteiraOpcoes}
                      getOptionLabel={o => `${o.adm} — ${o.nome}${o.codigo1 ? ` [${o.codigo1}]` : ''}`}
                      isOptionEqualToValue={(o, v) => o.adm === v.adm && o.codigo1 === v.codigo1 && o.nome === v.nome}
                      filterOptions={x => x}
                      value={entradaSelecionada}
                      onChange={(_, v) => setEntradaSelecionada(v)}
                      onInputChange={handleCarteiraBusca}
                      loading={buscandoCarteira}
                      noOptionsText={carteiraOpcoes.length === 0 ? 'Digite pelo menos 2 caracteres' : 'Nenhum resultado'}
                      renderInput={params => (
                        <TextField {...params} label="Buscar na carteira *" size="small"
                          placeholder="Nome ou código como aparece na carteira..." />
                      )}
                      renderOption={(props, o) => (
                        <li {...props} key={`${o.adm}|${o.codigo1 ?? ''}|${o.nome}`}>
                          <Box>
                            <Typography sx={{ fontSize: '13px' }}>{o.nome}</Typography>
                            <Typography sx={{ fontSize: '11px', color: '#64748B' }}>
                              {o.adm}{o.codigo1 ? ` · ${o.codigo1}` : ''}
                            </Typography>
                          </Box>
                        </li>
                      )}
                    />
                    {entradaSelecionada && (
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Chip label={entradaSelecionada.adm} size="small"
                          sx={{ fontSize: '11px', fontWeight: 700, backgroundColor: '#1E293B', color: '#fff', height: 22 }} />
                        {entradaSelecionada.codigo1 && (
                          <Typography sx={{ fontSize: '12px', fontFamily: 'monospace', color: '#475569' }}>
                            {entradaSelecionada.codigo1}
                          </Typography>
                        )}
                        <Typography sx={{ fontSize: '12px', color: '#94A3B8' }}>→</Typography>
                        <Typography sx={{ fontSize: '12px', color: '#475569' }}>
                          {entradaSelecionada.nome}
                        </Typography>
                      </Stack>
                    )}
                    {dpErro && (
                      <Typography sx={{ fontSize: '12px', color: '#DC2626' }}>{dpErro}</Typography>
                    )}
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" variant="outlined"
                        onClick={() => { setShowAddForm(false); setEntradaSelecionada(null); setCarteiraOpcoes([]); setDpErro(null); }}
                        sx={{ textTransform: 'none', fontSize: '12px' }}>
                        Cancelar
                      </Button>
                      <Button size="small" variant="contained"
                        disabled={savingDp || !entradaSelecionada}
                        onClick={handleSalvarDePara}
                        sx={{ textTransform: 'none', fontSize: '12px' }}>
                        {savingDp ? <CircularProgress size={14} /> : 'Salvar'}
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              ) : (
                <Button size="small" startIcon={<AddIcon sx={{ fontSize: 13 }} />}
                  onClick={() => setShowAddForm(true)}
                  sx={{ color: '#2C91AD', textTransform: 'none', fontSize: '12px', fontWeight: 500,
                    '&:hover': { backgroundColor: '#EFF6FF' }, mt: 0.5 }}>
                  Adicionar Mapeamento
                </Button>
              )}

            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// ─── GrupoAccordion ───────────────────────────────────────────────────────────

interface GrupoAccordionProps {
  grupo: GrupoFundo | null;
  ativos: AtivoMaster[];
  onEditAtivo: (ativo: AtivoMaster) => void;
  onNovoAtivo: (grupo: GrupoFundo | null) => void;
  onDeleteAtivo: (id: number) => void;
  onDeleteDePara: (id: number, ativoMasterId: number) => void;
  onAddDePara: (ativoMasterId: number, dp: DeParaAtivo) => void;
  onDeleteGrupo: (id: number) => void;
  onUpdateGrupo: (grupo: GrupoFundo) => void;
  defaultExpanded?: boolean;
  expandAtivoId?: number;
}

const emptyForm = { nome: '', cargo: '', email: '', telefone: '' };

const GrupoAccordion: React.FC<GrupoAccordionProps> = ({
  grupo, ativos, onEditAtivo, onNovoAtivo, onDeleteAtivo, onDeleteDePara, onAddDePara, onDeleteGrupo, onUpdateGrupo, defaultExpanded = false, expandAtivoId,
}) => {
  const temAtivos  = ativos.length > 0;
  const temFechado = ativos.some(a => a.condominio === 'FECHADO' || a.condominio_display === 'Fechado');
  const [contatos, setContatos] = useState<ContatoGrupoFundo[]>(grupo?.contatos ?? []);
  const [showContatoForm, setShowContatoForm] = useState(false);
  const [editingContato, setEditingContato] = useState<ContatoGrupoFundo | null>(null);
  const [contatoForm, setContatoForm] = useState(emptyForm);
  const [savingContato, setSavingContato] = useState(false);

  const [showGrupoDialog, setShowGrupoDialog] = useState(false);
  const [grupoForm, setGrupoForm] = useState({ gestor: grupo?.gestor ?? '', administrador: grupo?.administrador ?? '' });
  const [savingGrupo, setSavingGrupo] = useState(false);

  const handleAbrirEditarGrupo = () => {
    setGrupoForm({ gestor: grupo?.gestor ?? '', administrador: grupo?.administrador ?? '' });
    setShowGrupoDialog(true);
  };

  const handleCancelarGrupo = () => {
    setShowGrupoDialog(false);
  };

  const handleSalvarGrupo = async () => {
    if (!grupo) return;
    setSavingGrupo(true);
    try {
      const updated = await updateGrupoFundo(grupo.id, {
        gestor: grupoForm.gestor.trim() || undefined,
        administrador: grupoForm.administrador.trim() || undefined,
      });
      onUpdateGrupo(updated);
      setShowGrupoDialog(false);
    } finally {
      setSavingGrupo(false);
    }
  };

  const handleAbrirNovoContato = () => {
    setEditingContato(null);
    setContatoForm(emptyForm);
    setShowContatoForm(true);
  };

  const handleAbrirEditarContato = (c: ContatoGrupoFundo) => {
    setEditingContato(c);
    setContatoForm({ nome: c.nome, cargo: c.cargo ?? '', email: c.email ?? '', telefone: c.telefone ?? '' });
    setShowContatoForm(true);
  };

  const handleCancelarContato = () => {
    setShowContatoForm(false);
    setEditingContato(null);
    setContatoForm(emptyForm);
  };

  const handleSalvarContato = async () => {
    if (!contatoForm.nome.trim() || !grupo) return;
    setSavingContato(true);
    try {
      const payload = {
        grupo_fundo: grupo.id,
        nome: contatoForm.nome.trim(),
        cargo: contatoForm.cargo.trim() || undefined,
        email: contatoForm.email.trim() || undefined,
        telefone: contatoForm.telefone.trim() || undefined,
      };
      if (editingContato) {
        const updated = await updateContatoGrupoFundo(editingContato.id, payload);
        setContatos(prev => prev.map(c => c.id === updated.id ? updated : c));
      } else {
        const created = await createContatoGrupoFundo(payload);
        setContatos(prev => [...prev, created]);
      }
      handleCancelarContato();
    } finally {
      setSavingContato(false);
    }
  };

  const handleDeletarContato = async (id: number) => {
    if (!window.confirm('Remover este contato?')) return;
    await deleteContatoGrupoFundo(id);
    setContatos(prev => prev.filter(c => c.id !== id));
  };

  return (
    <>
    <Accordion defaultExpanded={defaultExpanded} disableGutters elevation={0} sx={{
      border: '1px solid #E2E8F0',
      borderRadius: '10px !important',
      mb: 1.5,
      overflow: 'hidden',
      '&:before': { display: 'none' },
      '&.Mui-expanded': { boxShadow: '0 4px 12px rgba(0,0,0,0.06)' },
    }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#94A3B8' }} />}
        sx={{
          backgroundColor: '#fff',
          px: 2.5, py: 0,
          minHeight: '64px !important',
          '& .MuiAccordionSummary-content': { my: 1.5 },
          '&:hover': { backgroundColor: '#F8FAFC' },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2} sx={{ flex: 1, mr: 1 }}>
          {/* Ícone */}
          <Box sx={{
            width: 36, height: 36, borderRadius: 2, flexShrink: 0,
            backgroundColor: grupo ? '#EFF6FF' : '#F1F5F9',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <AccountBalanceIcon sx={{ fontSize: 17, color: grupo ? '#1D4ED8' : '#94A3B8' }} />
          </Box>

          {/* Nome + tipo chip + CNPJ abaixo */}
          <Box sx={{ flex: '0 1 420px', minWidth: 0, overflow: 'hidden' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, overflow: 'hidden' }}>
              <Tooltip title={grupo?.nome ?? ''} placement="top" arrow disableHoverListener={!grupo?.nome}>
                <Typography sx={{
                  fontWeight: 700, fontFamily: 'Inter', fontSize: '14px', color: '#1E293B',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  cursor: 'default', minWidth: 0,
                }}>
                  {grupo?.nome ?? 'Sem Grupo'}
                </Typography>
              </Tooltip>
              {grupo?.tipo && (
                <Chip label={grupo.tipo} size="small" sx={{ ...getTipoChipSx(grupo.tipo), height: 20, fontSize: '11px', flexShrink: 0 }} />
              )}
            </Stack>
            {grupo?.cnpj && (
              <Typography sx={{ fontSize: '11px', color: '#94A3B8', fontFamily: 'monospace', mt: 0.25 }}>
                {grupo.cnpj}
              </Typography>
            )}
          </Box>

          {/* Direita: Gestor · Adm · contagem */}
          <Stack direction="row" alignItems="center" spacing={2} sx={{ flex: 1, justifyContent: 'flex-end' }}>
            {grupo?.gestor && (
              <Tooltip title={grupo.gestor} placement="top" arrow>
                <Typography sx={{
                  fontSize: '12px', color: '#64748B', whiteSpace: 'nowrap',
                  maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'default',
                }}>
                  <span style={{ color: '#94A3B8' }}>Gestor: </span>{grupo.gestor}
                </Typography>
              </Tooltip>
            )}
            {grupo?.administrador && (
              <Tooltip title={grupo.administrador} placement="top" arrow>
                <Typography sx={{
                  fontSize: '12px', color: '#64748B', whiteSpace: 'nowrap',
                  maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'default',
                }}>
                  <span style={{ color: '#94A3B8' }}>Adm: </span>{grupo.administrador}
                </Typography>
              </Tooltip>
            )}
            <Box sx={{
              px: 1.5, py: 0.5, borderRadius: 2,
              backgroundColor: !temAtivos ? '#F8FAFC' : temFechado ? '#F3EFF9' : '#EBF7FA',
              border: `1px solid ${!temAtivos ? '#E2E8F0' : temFechado ? '#C4AEE8' : '#9DD8E8'}`,
            }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 600,
                color: !temAtivos ? '#94A3B8' : temFechado ? '#7B5EA7' : '#2C91AD', fontFamily: 'Inter', whiteSpace: 'nowrap' }}>
                {ativos.length} {ativos.length === 1 ? 'ativo' : 'ativos'}
              </Typography>
            </Box>

            {grupo && (
              <Tooltip title="Editar grupo">
                <IconButton
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    handleAbrirEditarGrupo();
                  }}
                  sx={{ color: '#94A3B8', '&:hover': { color: '#2C91AD', backgroundColor: '#EFF6FF' } }}
                >
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}

            {grupo && (
              <Tooltip title={temAtivos ? `Não é possível excluir: o grupo possui ${ativos.length} ativo(s). Remova-os primeiro.` : 'Excluir grupo'}>
                <span>
                  <IconButton
                    size="small"
                    disabled={temAtivos}
                    onClick={e => {
                      e.stopPropagation();
                      if (window.confirm(`Excluir o grupo "${grupo.nome}"?`)) onDeleteGrupo(grupo.id);
                    }}
                    sx={{ color: '#94A3B8', '&:hover': { color: '#DC2626', backgroundColor: '#FEF2F2' } }}
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 0, borderTop: '1px solid #F1F5F9' }}>
        <TableContainer component={Paper} elevation={0}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...thSx, width: 44 }} />
                <TableCell sx={thSx}>Nome Canônico</TableCell>
                <TableCell sx={{ ...thSx, width: 130 }}>Classe FIDC</TableCell>
                <TableCell sx={{ ...thSx, width: 130 }}>Subclasse</TableCell>
                <TableCell sx={{ ...thSx, width: 190 }}>Rentabilidade</TableCell>
                <TableCell sx={{ ...thSx, width: 130 }}>Prazo Resgate</TableCell>
                <TableCell sx={{ ...thSx, width: 180 }}>Cronograma</TableCell>
                <TableCell sx={{ ...thSx, width: 80 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {ativos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#94A3B8', fontSize: '13px', fontStyle: 'italic' }}>
                    Nenhum ativo cadastrado neste grupo.
                  </TableCell>
                </TableRow>
              ) : (
                ativos.map(ativo => (
                  <AtivoRow key={ativo.id} ativo={ativo}
                    onEdit={onEditAtivo} onDelete={onDeleteAtivo} onDeleteDePara={onDeleteDePara}
                    onAddDePara={onAddDePara} initialExpanded={expandAtivoId === ativo.id} />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ px: 2, py: 1.25, backgroundColor: '#F8FAFC', borderTop: '1px solid #F1F5F9' }}>
          <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />}
            onClick={() => onNovoAtivo(grupo)}
            sx={{ color: '#2C91AD', textTransform: 'none', fontSize: '12px', fontWeight: 500,
              '&:hover': { backgroundColor: '#EFF6FF' } }}>
            Adicionar Ativo
          </Button>
        </Box>

        {/* Seção de Contatos */}
        {grupo && (
          <Box sx={{ px: 2.5, py: 2, borderTop: '1px solid #E2E8F0', backgroundColor: '#FAFAFA' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
              <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Contatos
              </Typography>
              {!showContatoForm && (
                <Button size="small" startIcon={<AddIcon sx={{ fontSize: 12 }} />}
                  onClick={handleAbrirNovoContato}
                  sx={{ color: '#2C91AD', textTransform: 'none', fontSize: '11px', fontWeight: 500, '&:hover': { backgroundColor: '#EFF6FF' } }}>
                  Adicionar
                </Button>
              )}
            </Stack>

            {contatos.length === 0 && !showContatoForm && (
              <Typography sx={{ fontSize: '12px', color: '#94A3B8', fontStyle: 'italic' }}>
                Nenhum contato cadastrado.
              </Typography>
            )}

            {contatos.length > 0 && (
              <Stack spacing={0.75} mb={showContatoForm ? 1.5 : 0}>
                {contatos.map(c => (
                  <Stack key={c.id} direction="row" alignItems="center" spacing={1.5}
                    sx={{ px: 1.5, py: 1, borderRadius: 1.5, backgroundColor: '#fff', border: '1px solid #E2E8F0',
                      '&:hover': { borderColor: '#CBD5E1', backgroundColor: '#F8FAFC' } }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#1E293B' }}>{c.nome}</Typography>
                      <Stack direction="row" spacing={1.5} flexWrap="wrap">
                        {c.cargo && <Typography sx={{ fontSize: '11px', color: '#64748B' }}>{c.cargo}</Typography>}
                        {c.email && <Typography sx={{ fontSize: '11px', color: '#2C91AD' }}>{c.email}</Typography>}
                        {c.telefone && <Typography sx={{ fontSize: '11px', color: '#64748B' }}>{c.telefone}</Typography>}
                      </Stack>
                    </Box>
                    <Stack direction="row" spacing={0.25}>
                      <Tooltip title="Editar contato">
                        <IconButton size="small" onClick={() => handleAbrirEditarContato(c)}
                          sx={{ color: '#94A3B8', '&:hover': { color: '#2C91AD', backgroundColor: '#EFF6FF' }, p: 0.5 }}>
                          <EditIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remover contato">
                        <IconButton size="small" onClick={() => handleDeletarContato(c.id)}
                          sx={{ color: '#94A3B8', '&:hover': { color: '#DC2626', backgroundColor: '#FEF2F2' }, p: 0.5 }}>
                          <DeleteIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            )}

            {showContatoForm && (
              <Box sx={{ p: 1.5, border: '1px dashed #CBD5E1', borderRadius: 1.5, backgroundColor: '#fff', mt: 0.5 }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1.5}>
                    <TextField size="small" label="Nome *" value={contatoForm.nome} fullWidth
                      onChange={e => setContatoForm(f => ({ ...f, nome: e.target.value }))} />
                    <TextField size="small" label="Cargo" value={contatoForm.cargo} fullWidth
                      onChange={e => setContatoForm(f => ({ ...f, cargo: e.target.value }))} />
                  </Stack>
                  <Stack direction="row" spacing={1.5}>
                    <TextField size="small" label="Email" value={contatoForm.email} fullWidth
                      onChange={e => setContatoForm(f => ({ ...f, email: e.target.value }))} />
                    <TextField size="small" label="Telefone" value={contatoForm.telefone} fullWidth
                      onChange={e => setContatoForm(f => ({ ...f, telefone: e.target.value }))} />
                  </Stack>
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button size="small" variant="outlined" onClick={handleCancelarContato}
                      sx={{ textTransform: 'none', fontSize: '12px' }}>
                      Cancelar
                    </Button>
                    <Button size="small" variant="contained"
                      disabled={savingContato || !contatoForm.nome.trim()}
                      onClick={handleSalvarContato}
                      sx={{ textTransform: 'none', fontSize: '12px' }}>
                      {savingContato ? <CircularProgress size={14} /> : editingContato ? 'Salvar' : 'Adicionar'}
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            )}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>

    {grupo && (
      <Dialog open={showGrupoDialog} onClose={handleCancelarGrupo} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Inter', fontSize: '16px', fontWeight: 700 }}>
          Editar Grupo Fundo
        </DialogTitle>
        <DialogContent>
          <Stack spacing={0.25} sx={{ mb: 2 }}>
            <Tooltip title={grupo.nome} placement="top" arrow>
              <Typography sx={{
                fontSize: '13px', fontWeight: 600, color: '#1E293B',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'default',
              }}>
                {grupo.nome}
              </Typography>
            </Tooltip>
            {grupo.cnpj && (
              <Typography sx={{ fontSize: '12px', color: '#94A3B8', fontFamily: 'monospace' }}>
                {grupo.cnpj}
              </Typography>
            )}
          </Stack>
          <Stack spacing={2}>
            <TextField size="small" label="Gestor" value={grupoForm.gestor} fullWidth autoFocus
              onChange={e => setGrupoForm(f => ({ ...f, gestor: e.target.value }))} />
            <TextField size="small" label="Administrador" value={grupoForm.administrador} fullWidth
              onChange={e => setGrupoForm(f => ({ ...f, administrador: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" onClick={handleCancelarGrupo} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button variant="contained" disabled={savingGrupo} onClick={handleSalvarGrupo} sx={{ textTransform: 'none' }}>
            {savingGrupo ? <CircularProgress size={16} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    )}
    </>
  );
};


// ─── Page ─────────────────────────────────────────────────────────────────────

type FiltroRapido = 'todos' | 'sem_mapeamento' | 'sem_rentabilidade' | 'sem_prazo' | 'sem_contatos' | 'sem_cronograma';

const FILTROS_RAPIDOS: { value: FiltroRapido; label: string }[] = [
  { value: 'todos',             label: 'Todos' },
  { value: 'sem_mapeamento',    label: 'Sem mapeamento' },
  { value: 'sem_rentabilidade', label: 'Sem rentabilidade' },
  { value: 'sem_prazo',         label: 'Sem prazo de resgate' },
  { value: 'sem_contatos',      label: 'Sem contatos' },
  { value: 'sem_cronograma',    label: 'Fechado sem cronograma' },
];

const MapeamentoAtivos: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Lidos uma única vez no mount e depois descartados — a URL é limpa imediatamente
  // para que o usuário possa navegar normalmente sem os params persistindo.
  const initialNavBusca   = useRef(searchParams.get('busca') ?? '').current;
  const initialNavAtivoId = useRef(
    searchParams.get('ativo_id') ? Number(searchParams.get('ativo_id')) : undefined
  ).current;

  useEffect(() => {
    if (initialNavBusca || initialNavAtivoId != null) {
      navigate('/ativos/mapeamento', { replace: true });
    }
  }, []);

  const [ativos, setAtivos] = useState<AtivoMaster[]>([]);
  const [gruposFundo, setGruposFundo] = useState<GrupoFundo[]>([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState(initialNavBusca);
  const [filtroRapido, setFiltroRapido] = useState<FiltroRapido>('todos');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [ativoEditando, setAtivoEditando] = useState<AtivoMaster | undefined>(undefined);
  const [grupoParaNovo, setGrupoParaNovo] = useState<GrupoFundo | null>(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const [ativosData, gruposData] = await Promise.all([getAtivosMAster(), getGruposFundo()]);
      setAtivos(ativosData);
      setGruposFundo(gruposData);
    } catch {
      setSnackbar({ open: true, message: 'Erro ao carregar dados.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const grupos = React.useMemo(() => {
    // 1. Filtro rápido
    const porFiltro =
      filtroRapido === 'sem_mapeamento'    ? ativos.filter(a => a.de_para.length === 0) :
      filtroRapido === 'sem_rentabilidade' ? ativos.filter(a =>
        !a.rent_tipo ||
        a.rent_valor == null ||
        (['IND_PERCENT', 'IND_SPREAD'].includes(a.rent_tipo) && !a.rent_indicador)
      ) :
      filtroRapido === 'sem_prazo'         ? ativos.filter(a => !a.prazo_resgate) :
      filtroRapido === 'sem_contatos'      ? ativos.filter(a => !a.grupo_fundo_detail?.contatos?.length) :
      filtroRapido === 'sem_cronograma'    ? ativos.filter(a => a.condominio === 'FECHADO' && (!a.cronogramas || a.cronogramas.length === 0)) :
      ativos;

    // 2. Busca textual
    const q = busca.trim().toLowerCase();
    let filtrados: AtivoMaster[];

    if (!q) {
      filtrados = porFiltro;
    } else {
      // Grupos que possuem ao menos um ativo que bate com a busca
      const gruposComMatch = new Set(
        porFiltro
          .filter(a =>
            a.nome_canonico.toLowerCase().includes(q) ||
            a.grupo_fundo_detail?.nome.toLowerCase().includes(q) ||
            a.de_para.some(dp =>
              dp.nome_bruto.toLowerCase().includes(q) ||
              dp.codigo_bruto.toLowerCase().includes(q)
            )
          )
          .map(a => a.grupo_fundo ?? null)
          .filter(k => k !== null)   // null tratado separado
      );

      filtrados = porFiltro.filter(a => {
        if (a.grupo_fundo == null) {
          // Sem grupo: só mostra quem bateu diretamente
          return (
            a.nome_canonico.toLowerCase().includes(q) ||
            a.de_para.some(dp =>
              dp.nome_bruto.toLowerCase().includes(q) ||
              dp.codigo_bruto.toLowerCase().includes(q)
            )
          );
        }
        // Com grupo: mostra todos do grupo se algum bateu
        return gruposComMatch.has(a.grupo_fundo);
      });
    }

    // 3. Agrupa
    const map = new Map<number | null, { grupo: GrupoFundo | null; ativos: AtivoMaster[] }>();
    for (const g of gruposFundo) map.set(g.id, { grupo: g, ativos: [] });
    map.set(null, { grupo: null, ativos: [] });

    for (const a of filtrados) {
      const key = a.grupo_fundo ?? null;
      if (!map.has(key)) map.set(key, { grupo: a.grupo_fundo_detail ?? null, ativos: [] });
      map.get(key)!.ativos.push(a);
    }

    // Remove grupos vazios
    Array.from(map.keys()).forEach(key => {
      if (map.get(key)!.ativos.length === 0) map.delete(key);
    });

    return Array.from(map.values()).sort((a, b) => {
      if (!a.grupo) return 1;
      if (!b.grupo) return -1;
      return a.grupo.nome.localeCompare(b.grupo.nome);
    });
  }, [ativos, gruposFundo, busca, filtroRapido]);

  const totalMapeamentos = ativos.reduce((acc, a) => acc + a.de_para.length, 0);
  const semMapeamento = ativos.filter(a => a.de_para.length === 0).length;

  const handleNovoAtivo = (grupo: GrupoFundo | null) => {
    setAtivoEditando(undefined);
    setGrupoParaNovo(grupo);
    setModalOpen(true);
  };

  const handleEditarAtivo = (ativo: AtivoMaster) => {
    setAtivoEditando(ativo);
    setGrupoParaNovo(null);
    setModalOpen(true);
  };

  const handleSalvo = (ativo: AtivoMaster) => {
    setAtivos(prev => {
      const idx = prev.findIndex(a => a.id === ativo.id);
      if (idx >= 0) { const next = [...prev]; next[idx] = ativo; return next; }
      return [...prev, ativo];
    });
    setSnackbar({ open: true, message: 'Ativo salvo com sucesso.', severity: 'success' });
  };

  const handleDeletarAtivo = async (id: number) => {
    try {
      await deleteAtivoMaster(id);
      setAtivos(prev => prev.filter(a => a.id !== id));
      setSnackbar({ open: true, message: 'Ativo excluído.', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Erro ao excluir ativo.', severity: 'error' });
    }
  };

  const handleDeletarDePara = async (deParaId: number, ativoMasterId: number) => {
    try {
      await deleteDeParaAtivo(deParaId);
      setAtivos(prev =>
        prev.map(a => a.id === ativoMasterId
          ? { ...a, de_para: a.de_para.filter(dp => dp.id !== deParaId) }
          : a
        )
      );
      setSnackbar({ open: true, message: 'Mapeamento removido.', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Erro ao remover mapeamento.', severity: 'error' });
    }
  };

  const handleDeletarGrupo = async (id: number) => {
    try {
      await deleteGrupoFundo(id);
      setGruposFundo(prev => prev.filter(g => g.id !== id));
      setSnackbar({ open: true, message: 'Grupo excluído.', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Erro ao excluir grupo.', severity: 'error' });
    }
  };

  const handleAtualizarGrupo = (grupoAtualizado: GrupoFundo) => {
    setGruposFundo(prev => prev.map(g => g.id === grupoAtualizado.id ? grupoAtualizado : g));
    setSnackbar({ open: true, message: 'Grupo atualizado com sucesso.', severity: 'success' });
  };

  const handleAddDePara = (ativoMasterId: number, dp: DeParaAtivo) => {
    setAtivos(prev =>
      prev.map(a => a.id === ativoMasterId
        ? { ...a, de_para: [...a.de_para, dp] }
        : a
      )
    );
    setSnackbar({ open: true, message: 'Mapeamento adicionado.', severity: 'success' });
  };

  return (
    <Box sx={{ p: 3, fontFamily: 'Inter, sans-serif', maxWidth: 1400 }}>

      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: 'Inter', color: '#0F172A', mb: 0.5 }}>
            Mapeamento de Ativos
          </Typography>
          <Typography variant="body2" sx={{ color: '#94A3B8' }}>
            Ativos canônicos e seus mapeamentos por administradora
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => handleNovoAtivo(null)}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, boxShadow: 'none',
            '&:hover': { boxShadow: '0 4px 12px rgba(44,145,173,0.3)' } }}>
          Novo Ativo
        </Button>
      </Stack>

      {/* Stats + Busca */}
      {!loading && (
        <Stack spacing={1.5} mb={3}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Stack direction="row" spacing={1.5}>
              <StatBox label="Grupos" value={grupos.length} minWidth={100} />
              <StatBox label="Ativos" value={ativos.length} valueColor="#1D4ED8" bg="#EFF6FF" minWidth={100} />
              <StatBox label="Mapeamentos" value={totalMapeamentos} valueColor="#15803D" bg="#F0FDF4" minWidth={100} />
              {semMapeamento > 0 && (
                <StatBox label="Sem mapeamento" value={semMapeamento} valueColor="#B45309" bg="#FFFBEB" minWidth={100} />
              )}
            </Stack>
            <TextField
              placeholder="Buscar por nome, grupo, código ou nome bruto..."
              size="small"
              value={busca}
              onChange={e => setBusca(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94A3B8' }} /></InputAdornment> }}
              sx={{ width: 380, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {FILTROS_RAPIDOS.map(f => (
              <Chip
                key={f.value}
                label={f.value === 'sem_mapeamento'    ? `${f.label} (${semMapeamento})` :
                       f.value === 'sem_rentabilidade' ? `${f.label} (${ativos.filter(a => !a.rent_tipo || a.rent_valor == null || (['IND_PERCENT', 'IND_SPREAD'].includes(a.rent_tipo!) && !a.rent_indicador)).length})` :
                       f.value === 'sem_prazo'         ? `${f.label} (${ativos.filter(a => !a.prazo_resgate).length})` :
                       f.value === 'sem_contatos'      ? `${f.label} (${new Set(ativos.filter(a => a.grupo_fundo != null && !a.grupo_fundo_detail?.contatos?.length).map(a => a.grupo_fundo)).size})` :
                       f.value === 'sem_cronograma'    ? `${f.label} (${ativos.filter(a => a.condominio === 'FECHADO' && (!a.cronogramas || a.cronogramas.length === 0)).length})` :
                       f.label}
                size="small"
                onClick={() => setFiltroRapido(f.value)}
                color={filtroRapido === f.value ? 'primary' : 'default'}
                variant={filtroRapido === f.value ? 'filled' : 'outlined'}
                sx={{ fontSize: '12px', fontFamily: 'Inter', cursor: 'pointer' }}
              />
            ))}
          </Stack>
        </Stack>
      )}

      {/* Conteúdo */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      ) : grupos.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          {busca ? 'Nenhum ativo encontrado para a busca.' : 'Nenhum ativo cadastrado ainda. Use o botão "Novo Ativo" ou o ⚠ na Carteira Diária.'}
        </Alert>
      ) : (
        grupos.map(({ grupo, ativos: ativosGrupo }) => (
          <GrupoAccordion
            key={grupo?.id ?? 'sem-grupo'}
            grupo={grupo}
            ativos={ativosGrupo}
            onEditAtivo={handleEditarAtivo}
            onNovoAtivo={handleNovoAtivo}
            onDeleteAtivo={handleDeletarAtivo}
            onDeleteDePara={handleDeletarDePara}
            onAddDePara={handleAddDePara}
            onDeleteGrupo={handleDeletarGrupo}
            onUpdateGrupo={handleAtualizarGrupo}
            defaultExpanded={initialNavAtivoId != null && ativosGrupo.some(a => a.id === initialNavAtivoId)}
            expandAtivoId={initialNavAtivoId}
          />
        ))
      )}

      <CadastrarAtivoMasterModal
        isOpen={modalOpen}
        onRequestClose={() => setModalOpen(false)}
        ativoMasterExistente={ativoEditando}
        grupoPreSelecionado={grupoParaNovo ?? undefined}
        onSalvo={handleSalvo}
      />

      <Snackbar open={snackbar.open} autoHideDuration={3500}
        onClose={() => setSnackbar(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(p => ({ ...p, open: false }))}
          sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MapeamentoAtivos;
